import React, { forwardRef, useContext, useCallback } from 'react';
import { styled, css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { fg } from '@confluence/feature-gating';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/getIsNav4Enabled.ts';
import { DISCOVER_ITEM_ON_HOME_PAGE_CLICK } from '@confluence/browser-metrics/entry-points/viewPageTransitionSourceConstants.ts';
import { setViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { generatePathWithFeatureCodesQueryParam } from '@confluence/feature-codes/entry-points/featureCodes.ts';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';
import { LiveDocsIconTile } from '@confluence/live-pages-features/entry-points/LiveDocsIconTile.ts';

import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../Feed.tsx';

import { FeedCardContext } from './FeedCardContext.ts';

const styles = css({ verticalAlign: 'bottom' });
const tooltipContainerSelector = 'feed.TooltipContainer';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TooltipContainer = styled.div<{ 'data-styled-selector'?: string }>({
	display: 'flex',
	overflow: 'hidden',
});
TooltipContainer.defaultProps = {
	'data-styled-selector': tooltipContainerSelector,
};

const TooltipTag = forwardRef<any, React.PropsWithChildren<{}>>((props, ref) => {
	const { children, ...rest } = props;
	return (
		<TooltipContainer {...rest} ref={ref}>
			{children}
		</TooltipContainer>
	);
});

const titleSelector = 'feed.Title';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.a<{ 'data-styled-selector'?: string }>({
	position: 'relative',
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	paddingRight: token('space.050'),
	margin: token('space.050'),
});
Title.defaultProps = {
	'data-styled-selector': titleSelector,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',

	/* CSS specificity fix. turns default blue if it goes directly in the a tag */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`> [data-styled-selector="${tooltipContainerSelector}"] [data-styled-selector="${titleSelector}"]`]:
		{
			color: token('color.text'),
			'&:visited': {
				color: token('color.text.discovery'),
			},
			'&:hover': {
				color: token('color.text.subtle'),
				textDecoration: 'none',
			},
		},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentIconWrapper = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[data-css-target="emoji-placeholder"]': {
		height: '16px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		div: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			marginRight: `${token('space.0')} !important`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	height: '16px',
	width: '16px',
	font: token('font.body.large'),
	margin: '0px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		margin: '0px',
	},
});

type Props = {
	contentTitle: string;
	contentUrl: string;
	contentType: string;
	contentSubType?: string | null;
	emoji?: string;
	customIconUrl?: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
	hasAiSnippet?: boolean;
};

// dont allow clicking or focus because the whole card is a link, but we use :visited to color the title
export const FeedCardTitle = ({
	contentTitle,
	contentUrl,
	contentType,
	contentSubType,
	emoji,
	customIconUrl,
	popularFeedTimeGranularity,
	followingFeedSortby,
	hasAiSnippet,
}: Props) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);
	const { push } = useRouteActions();
	const { trackAIResultAction } = useAIEventsInstrumentation();

	const feedType = popularFeedTimeGranularity ? 'popularFeed' : 'followingFeed';

	const handleLinkClick = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			if (hasAiSnippet) {
				trackAIResultAction(`${feedType}aiSnippetClicked`);
			}

			if (contentType === 'page') {
				setViewTransitionSource(DISCOVER_ITEM_ON_HOME_PAGE_CLICK);
			}
			fireAnalyticsEvent({
				type: 'sendUIEvent',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'feedCard',
				includeNavdex: true,
				additionalAttributes: {
					popularFeedTimeGranularity,
					followingFeedSortby,
				},
			});

			if (fg('confluence_frontend_insights_feature_attribution')) {
				e.preventDefault();
				if (e.metaKey || e.ctrlKey) {
					window.open(generatePathWithFeatureCodesQueryParam(contentUrl, [FeatureCode.HOME_FEED]));
				} else {
					push(generatePathWithFeatureCodesQueryParam(contentUrl, [FeatureCode.HOME_FEED]));
				}
			}
		},
		[
			contentType,
			fireAnalyticsEvent,
			popularFeedTimeGranularity,
			followingFeedSortby,
			contentUrl,
			push,
			feedType,
			hasAiSnippet,
			trackAIResultAction,
		],
	);

	const icon = getIcon(contentType, contentSubType, customIconUrl, emoji);

	return (
		<Wrapper>
			<ContentIconWrapper>{icon}</ContentIconWrapper>
			<Tooltip content={contentTitle} tag={TooltipTag}>
				<Title href={contentUrl} onClick={handleLinkClick} data-testid="feed-card-title-link">
					{contentTitle}
				</Title>
			</Tooltip>
		</Wrapper>
	);
};

const getIcon = (
	contentType: string,
	contentSubType?: string | null,
	customIconUrl?: string,
	emoji?: string,
): React.ReactNode => {
	const isLivePage = contentType === 'page' && contentSubType === 'live';
	const isNav4Enabled = getIsNav4Enabled();
	const color = isLivePage ? token('color.icon.accent.magenta') : token('color.icon.accent.blue');

	if (customIconUrl) {
		return (
			<img alt="" src={customIconUrl} width="16" height="16" aria-hidden="true" css={styles} />
		);
	}

	if (isNav4Enabled) {
		return <ContentIcon type={contentType} emoji={emoji} subType={contentSubType} color={color} />;
	}

	if (emoji) {
		return (
			<LazyEmojiComponentLoader
				emoji={emoji}
				context="feedCardBodyIcon"
				height={16}
				wrapper={EmojiContainer}
			/>
		);
	}

	return isLivePage ? (
		<LiveDocsIconTile label="live-doc-tile" testId="live-doc-icon-tile-feed-card" size="small" />
	) : (
		<ContentIcon type={contentType} />
	);
};
